// Phase 4.3 Dashboard Zod Schemas
// Wire field is widgetKey (Agent 2 / dash-load-fail-1.md). Envelope unwrapped in api.ts.
import { z } from "zod";
import { WIDGET_ALLOWLIST } from "./widget-allowlist";

/** Mutation / template allowlist — matches BE widget-allowlist.keys.json. */
export const WidgetTypeSchema = z.enum(
  WIDGET_ALLOWLIST as unknown as [string, ...string[]],
);

export const DashboardVisibilitySchema = z.enum(["WORKSPACE", "ORG", "PRIVATE"]);

export const DashboardLayoutSchema = z.object({
  x: z.number().int().min(0).max(11),
  y: z.number().int().min(0),
  w: z.number().int().min(1).max(12),
  h: z.number().int().min(1).max(20),
});

const DEFAULT_LAYOUT = { x: 0, y: 0, w: 4, h: 3 };

/**
 * Strict shape after normalizing the wire field to `widgetKey`.
 * Read path uses the resilient parser so one bad card never fails the dashboard.
 */
export const DashboardWidgetSchema = z.object({
  id: z.string().uuid(),
  widgetKey: z.string().min(1),
  title: z.string().min(1).max(200),
  layout: DashboardLayoutSchema,
  config: z.record(z.string(), z.any()),
  dataSource: z.string().optional(),
  unsupported: z.boolean().optional(),
});

const SharedDashboardWidgetStrictSchema = z.object({
  id: z.string().uuid(),
  widgetKey: z.string().min(1),
  title: z.string().min(1).max(200),
  layout: DashboardLayoutSchema,
  config: z.record(z.string(), z.any()).default({}),
  unsupported: z.boolean().optional(),
});

/**
 * Prefer `widgetKey` (canonical). Accept legacy FE `type` only as a fall-back
 * so older fixtures still parse; never require `type` on the wire.
 */
function normalizeWidgetInput(raw: unknown): unknown {
  if (!raw || typeof raw !== "object") return raw;
  const obj = raw as Record<string, unknown>;
  const widgetKey =
    (typeof obj.widgetKey === "string" && obj.widgetKey) ||
    (typeof obj.type === "string" && obj.type) ||
    undefined;
  if (widgetKey === undefined) return raw;
  const { type: _type, ...rest } = obj;
  return { ...rest, widgetKey };
}

function unsupportedPlaceholder(raw: unknown): z.infer<typeof DashboardWidgetSchema> {
  const obj =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const id =
    typeof obj.id === "string" && obj.id.length > 0
      ? obj.id
      : "00000000-0000-4000-8000-000000000000";
  const layoutParsed = DashboardLayoutSchema.safeParse(obj.layout);
  return {
    id,
    widgetKey:
      (typeof obj.widgetKey === "string" && obj.widgetKey) ||
      (typeof obj.type === "string" && obj.type) ||
      "__invalid__",
    title:
      typeof obj.title === "string" && obj.title.trim().length > 0
        ? obj.title
        : "Unsupported card",
    layout: layoutParsed.success ? layoutParsed.data : DEFAULT_LAYOUT,
    config:
      obj.config && typeof obj.config === "object" && !Array.isArray(obj.config)
        ? (obj.config as Record<string, unknown>)
        : {},
    unsupported: true,
  };
}

function parseWidgetResilient(raw: unknown): z.infer<typeof DashboardWidgetSchema> {
  const normalized = normalizeWidgetInput(raw);
  const parsed = DashboardWidgetSchema.safeParse(normalized);
  if (parsed.success) return parsed.data;
  return unsupportedPlaceholder(normalized);
}

function parseSharedWidgetResilient(
  raw: unknown,
): z.infer<typeof SharedDashboardWidgetStrictSchema> {
  const normalized = normalizeWidgetInput(raw);
  const parsed = SharedDashboardWidgetStrictSchema.safeParse(normalized);
  if (parsed.success) return parsed.data;
  const fallback = unsupportedPlaceholder(normalized);
  return { ...fallback, config: fallback.config ?? {} };
}

/** Per-widget resilient array — unknown/invalid cards become unsupported slots. */
export const DashboardWidgetsArraySchema = z
  .array(z.unknown())
  .transform((items) => items.map(parseWidgetResilient));

export const SharedDashboardWidgetsArraySchema = z
  .array(z.unknown())
  .transform((items) => items.map(parseSharedWidgetResilient));

/** @deprecated Prefer SharedDashboardWidgetsArraySchema for entity parse. */
export const SharedDashboardWidgetSchema = SharedDashboardWidgetStrictSchema;

export const DashboardEntitySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  description: z.string().optional().nullable(),
  visibility: DashboardVisibilitySchema,
  workspaceId: z.string().uuid().nullable().optional(),
  widgets: DashboardWidgetsArraySchema.default([]),
  createdAt: z.union([z.string(), z.coerce.string()]),
  updatedAt: z.union([z.string(), z.coerce.string()]),
  shareToken: z.string().uuid().nullable().optional(),
  shareEnabled: z.boolean().optional(),
  shareExpiresAt: z.string().nullable().optional(),
}).transform((d) => ({
  ...d,
  description: d.description ?? undefined,
  workspaceId: d.workspaceId ?? "",
  createdAt: String(d.createdAt),
  updatedAt: String(d.updatedAt),
}));

/** Sidebar availability list row — list payloads omit widgets/detail fields. */
export const DashboardListRefSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
});

export const SharedDashboardSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  description: z.string().optional().nullable(),
  visibility: DashboardVisibilitySchema.optional(),
  widgets: SharedDashboardWidgetsArraySchema,
});

export const DashboardTemplateSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().optional(),
  persona: z.string(),
  methodology: z.string().optional(),
  definition: z.object({
    widgets: z.array(
      z.object({
        widgetKey: WidgetTypeSchema,
        title: z.string(),
        config: z.record(z.string(), z.any()),
        layout: DashboardLayoutSchema,
      })
    ),
  }),
});

export const AISuggestResponseSchema = z.object({
  templateKey: z.string(),
  widgetSuggestions: z.array(WidgetTypeSchema),
});

export const AIGenerateResponseSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  visibility: DashboardVisibilitySchema,
  widgets: z.array(
    z.object({
      widgetKey: WidgetTypeSchema,
      title: z.string(),
      config: z.record(z.string(), z.any()),
      layout: DashboardLayoutSchema,
    })
  ),
});

// Custom error for workspace required
export class WorkspaceRequiredError extends Error {
  constructor() {
    super("Workspace ID is required. Please select a workspace.");
    this.name = "WorkspaceRequiredError";
  }
}
